/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { getStoredSupportSession } from "@/features/support-access/support-session";
import type { UserManager } from "oidc-client-ts";
import * as oidcClient from "oidc-client-ts";
import { ReactNode, useEffect, useState, useRef } from "react";
import { AuthProvider, useAuth as _useAuth } from "react-oidc-context";
import { fastContext } from "@/shared/state";
import { useLoading } from "./LoadingContext";
import { globalErrorType, useGlobalError } from "./ErrorContext";
import { getRequiredEnvVar } from "@shared/utils/validate-env";
import { navigateToProtectedRoute as navigateToProtectedRouteHelper } from "./authentication-navigation";
import { handleSigninFlow, handleSignoutFlow } from "./authentication-flows";
import { processAuthEffect } from "./authentication-use-effect";
import { MockAuthenticationProvider, useAuth as useMockAuth } from "./authentication-mock";
import { resolveOidcRedirectUri } from "@shared/utils/native-oidc";
import { ensureRoutableSigninState } from "@shared/utils/oidc-signin-state-repair";
import { registerNativeOidcDeeplinkListener } from "@shared/utils/native-oidc-deeplink";
import { getRealLocalStorage } from "@shared/utils/storage/localStorageWrapper";

// Check if mock mode is enabled.
//
// Read FF_MOCK_AUTH through the module NAMESPACE (not a destructured `const`
// binding) and resolve it via a function called at RENDER/CALL time. Under
// isolate:false the feature-flag module is mocked worker-wide by the global
// setup (FF_MOCK_AUTH: true) while each authentication.*.test file overrides it
// with FF_MOCK_AUTH: false. A destructured `const FF_MOCK_AUTH` captures ONE of
// those at module-eval time and freezes it for the whole worker, so the branch
// (mock provider vs real provider) became ordering-dependent. Reading
// `featureFlags.FF_MOCK_AUTH` on demand always reflects the resolved mock
// module's current value, making the branch deterministic regardless of which
// suite evaluated authentication.tsx first.
import * as featureFlags from "@shared/utils/feature-flag";
import { ENVIRONMENT } from "@shared/utils/environment";

/**
 * Resolve whether mock authentication is active, reading the flag at call time.
 *
 * @returns True when FF_MOCK_AUTH is enabled on the resolved feature-flag module.
 */
export function isMockAuthEnabled(): boolean {
  return Boolean(featureFlags.FF_MOCK_AUTH);
}

const USE_MOCK_AUTH = isMockAuthEnabled();

if (USE_MOCK_AUTH && ENVIRONMENT !== 'local' && process.env.NODE_ENV !== 'test') {
  throw new Error(
    '[FATAL] Mock authentication is forbidden outside local environment. ' +
    `Current environment: ${ENVIRONMENT}. ` +
    'This is a security violation. Check your configuration.'
  );
}

// OIDC configuration + UserManager are initialized LAZILY (on first access),
// NOT at module load. Under isolate:false the module is evaluated once per
// worker; if the first sibling test to import it had FF_MOCK_AUTH === true, an
// eager `if (!USE_MOCK_AUTH)` block would leave UserAuthManager permanently
// undefined for the whole worker, so a later file with FF_MOCK_AUTH === false
// (e.g. authentication.hooks) would hit `performSignout`'s `!UserAuthManager`
// guard and throw before writing returnPath. Deferring construction to the
// first non-mock access reads FF_MOCK_AUTH live and heals that poisoning.
let REACT_APP_AUTHORITY_URL: string | undefined;
let REACT_APP_CLIENT_ID: string | undefined;
let oidcConfiguration: any;
let _userAuthManager: UserManager | undefined;

/**
 * Builds the OIDC client configuration from environment variables, wiring the
 * authority, client id, redirect URI, and localStorage-backed user/state
 * stores. Called lazily on first non-mock access.
 *
 * @returns The oidc-client-ts configuration object.
 */
function buildOidcConfiguration(): any {
  REACT_APP_AUTHORITY_URL = getRequiredEnvVar('REACT_APP_AUTHORITY_URL');
  REACT_APP_CLIENT_ID = getRequiredEnvVar('REACT_APP_CLIENT_ID');
  return {
    authority: REACT_APP_AUTHORITY_URL,
    client_id: REACT_APP_CLIENT_ID,
    redirect_uri: resolveOidcRedirectUri(),
    response_type: "code",
    scope: "openid",
    userStore: new oidcClient.WebStorageStateStore({
      prefix: "oidc:user",
      store: window.localStorage,
    }),
    stateStore: new oidcClient.WebStorageStateStore({
      prefix: "oidc:state",
      store: window.localStorage,
    }),
    loadUserInfo: true,
    // A support session has no refresh token; a silent renew would fall back
    // to a prompt=none iframe and could swap in whoever is signed in to
    // Authien in this browser. It simply ends when the token expires.
    automaticSilentRenew: getStoredSupportSession() === null,
  };
}

/**
 * Lazily resolve the OIDC UserManager, reading FF_MOCK_AUTH at CALL time.
 *
 * @returns The UserManager in real-auth mode, or undefined in mock mode.
 */
export function getUserAuthManager(): UserManager | undefined {
  if (isMockAuthEnabled()) {
    return undefined;
  }
  if (!oidcConfiguration) {
    oidcConfiguration = buildOidcConfiguration();
  }
  if (!_userAuthManager) {
    _userAuthManager = new oidcClient.UserManager(oidcConfiguration);
  }
  return _userAuthManager;
}

/**
 * Lazily resolve the OIDC configuration, reading FF_MOCK_AUTH at CALL time.
 *
 * @returns The OIDC configuration object, or undefined in mock mode.
 */
export function getOidcConfiguration(): any {
  if (isMockAuthEnabled()) {
    return undefined;
  }
  if (!oidcConfiguration) {
    oidcConfiguration = buildOidcConfiguration();
  }
  return oidcConfiguration;
}

/**
 * Test-only: drop the cached UserManager and OIDC configuration so the next
 * `getUserAuthManager()` rebuilds them from the currently-active `oidc-client-ts`
 * mock. Under Vitest `isolate: false` the module singletons persist across files
 * in one worker; the FIRST test to instantiate `_userAuthManager` binds it to
 * whatever `UserManager` mock was active then, poisoning siblings that expect
 * their own shared spies (e.g. `removeUser` never called). The global lifecycle
 * `beforeEach` calls this to guarantee each test gets a manager wired to the
 * mock it registered. No-op cost in production (never called there).
 *
 * @returns {void}
 */
export function resetUserAuthManagerCache(): void {
  _userAuthManager = undefined;
  oidcConfiguration = undefined;
}

/**
 * Backward-compatible UserManager binding. Property access is forwarded lazily
 * to `getUserAuthManager()` so callers that do `UserAuthManager.removeUser()`
 * resolve the real manager on demand — reading FF_MOCK_AUTH live and healing
 * the isolate:false module-poisoning that a static export would freeze in.
 * In mock mode the underlying manager is undefined; calling a method throws,
 * which matches the intended "no real auth in mock mode" contract.
 */
export const UserAuthManager: UserManager = new Proxy({} as UserManager, {
  get(_target, property: string | symbol) {
    const manager = getUserAuthManager();
    const value = (manager as unknown as Record<string | symbol, unknown>)?.[property];
    return typeof value === "function" ? value.bind(manager) : value;
  },
});

/**
 * Fast context for authentication state validation.
 */
export const validateAuthContext = fastContext;

import {
  validateToken,
  handleTokenValidationResult,
  handleTokenValidationError
} from "./authentication-provider-helpers";

/**
 * Provides OIDC authentication context, delegating to mock or real provider based on FF_MOCK_AUTH.
 *
 * @param props - Component props with children
 * @returns The authentication provider element
 */
export const AuthenticationProvider: ComponentWithChildren = ({ children }) => {
  // Read the flag live at render time (not the module-load-time capture) so that
  // under isolate:false a sibling test's feature-flag mock is honored regardless
  // of module load ordering — the module-level USE_MOCK_AUTH stays only for the
  // import-time security guard and OIDC initialization below.
  if (isMockAuthEnabled()) {
    return <MockAuthenticationProvider>{children}</MockAuthenticationProvider>;
  }

  const { addLoading, removeLoading } = useLoading((state) => state);
  const { setGlobalError } = useGlobalError((state) => state);

  useEffect(() => {
    addLoading();
    validateToken()
      .then((result) => {
        if (!result) {
          handleTokenValidationResult();
        }
      })
      .catch((err) => handleTokenValidationError(err, setGlobalError))
      .finally(() => removeLoading());
  }, []);

  // Native sign-in fix: repair the pending OIDC signin-state's `request_type`
  // before react-oidc-context's AuthProvider calls UserManager.signinCallback(),
  // so a native deeplink round-trip cannot throw "invalid response_type in
  // state" and bounce the user to landing. Routes into signinRedirectCallback,
  // which still fully validates the PKCE code exchange + state + nonce. No-op on
  // the web happy path and when no pending callback state exists.
  ensureRoutableSigninState(window.location.href, getRealLocalStorage());

  // Native sign-in fix: on the Capacitor shell the OIDC redirect_uri is the
  // app deeplink `br.com.k4d.housien://authentication/callback` (NOT the dead
  // `https://localhost`). Android routes that scheme back into the app; this
  // listener receives the deeplink and feeds it to UserManager.signinCallback
  // to complete the PKCE exchange, then navigates to the in-app callback route.
  // No-op on the web (isNativePlatform() is false).
  useEffect(() => {
    const cleanup = registerNativeOidcDeeplinkListener();
    return cleanup;
  }, []);

  return (
    <AuthProvider
      {...getOidcConfiguration()}
      onSigninCallback={() => {
        window.history.replaceState({}, document.title, window.location.pathname);
      }}
      onSigninError={(error: Error) => {
        console.error('[OIDC] signinCallback error:', error);
      }}
    >
      {children}
    </AuthProvider>
  );
};


/**
 * useAuth function.
 */
export const useAuth = () => {
  // Read the flag live at render time (not the module-load-time capture) so a
  // sibling test's feature-flag mock is honored under isolate:false.
  if (isMockAuthEnabled()) {
    return useMockAuth();
  }

  const auth = _useAuth();
  const { setGlobalError } = useGlobalError((state) => state);
  const { addLoading, removeLoading } = useLoading((state) => state);
  const [isAuthenticated, setIsAuthenticated] = useState(auth.isAuthenticated);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const hasCheckedAuth = useRef(false);

  useEffect(() => {
    processAuthEffect(
      auth,
      isAuthenticated,
      hasCheckedAuth,
      setIsCheckingAuth,
      addLoading,
      setIsAuthenticated,
      setGlobalError,
      removeLoading
    );
  }, [auth.isAuthenticated, auth.user, auth.isLoading]);

  const signin = () => handleSigninFlow(addLoading, removeLoading, setGlobalError);
  const signout = () => handleSignoutFlow(addLoading, removeLoading, setGlobalError);

  /**
   * Navigate to a protected route. If user is not authenticated, redirects to signin.
   * This prevents blank pages and improves UX by showing loading immediately.
   * 
   * The key improvement: Instead of navigating to the protected route and then
   * redirecting (which causes a blank page), we check authentication first.
   * If not authenticated, we call signin() directly which shows loading immediately.
   * 
   * @param path - The protected route path to navigate to
   * @param navigate - React Router navigate function
   */
  const navigateToProtectedRoute = (path: string, navigate: (path: string) => void): void => {
    navigateToProtectedRouteHelper(
      path,
      navigate,
      signin,
      auth.isLoading,
      isCheckingAuth,
      isAuthenticated,
      auth.isAuthenticated
    );
  };

  return {
    ...auth,
    isAuthenticated,
    isLoading: auth.isLoading || isCheckingAuth, // Include our checking state
    signin,
    signout,
    navigateToProtectedRoute: navigateToProtectedRoute,
  };
};

export type ProtectedRouteProps = {
  element: ReactNode;
};

export default AuthenticationProvider;
